import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputSwitch } from 'primereact/inputswitch';
import { Card } from 'primereact/card';
import { ClassAttributeList } from '../../../../../../api/types/settings/settings-all';
import { ClassAttributeType } from '../../../../../../api/types/settings/class-attributes/class-attribute-types';
import globalSettingsBranches from '../../../../../../stores/global-settings-branches';
import { toast } from 'react-toastify';
import { addNewClassListAttributesValuesOperations } from '../operations/add-new-class-list-attributes-values-operations';

interface AddClassAttributesDialogProps {
  visible: boolean;
  onHide: () => void;
  buildingId: number;
  classId: number;
  className: string;
  onSuccess?: () => void;
}

interface AttributeValue {
  attributeId: number;
  numericValue?: number;
  boolValue?: boolean;
}

export const AddClassAttributesValuesDialog: React.FC<
  AddClassAttributesDialogProps
> = ({ visible, onHide, buildingId, classId, className, onSuccess }) => {
  const [selectedList, setSelectedList] = useState<ClassAttributeList | null>(
    null,
  );
  const [attributeValues, setAttributeValues] = useState<
    Record<number, AttributeValue>
  >({});
  const [isLoading, setIsLoading] = useState(false);

  const classAttributeLists = globalSettingsBranches.classAttributes2;

  useEffect(() => {
    if (visible) {
      // Reset form when dialog opens
      setSelectedList(null);
      setAttributeValues({});
    }
  }, [visible]);

  useEffect(() => {
    if (selectedList) {
      // Initialize attribute values when list is selected
      const initialValues: Record<number, AttributeValue> = {};
      selectedList.attributes.forEach((attr) => {
        initialValues[attr.id] = {
          attributeId: attr.id,
          numericValue:
            attr.attributeType === ClassAttributeType.Numeric ? 0 : undefined,
          boolValue:
            attr.attributeType === ClassAttributeType.Bool ? false : undefined,
        };
      });
      setAttributeValues(initialValues);
    }
  }, [selectedList]);

  const handleAttributeValueChange = (
    attributeId: number,
    value: number | boolean | null,
  ) => {
    setAttributeValues((prev) => ({
      ...prev,
      [attributeId]: {
        ...prev[attributeId],
        numericValue:
          typeof value === 'number' ? value : prev[attributeId]?.numericValue,
        boolValue:
          typeof value === 'boolean' ? value : prev[attributeId]?.boolValue,
      },
    }));
  };

  const handleSubmit = async () => {
    if (!selectedList) {
      toast.error('יש לבחור רשימת תכונות');
      return;
    }

    const values = Object.values(attributeValues);
    if (values.length === 0) {
      toast.error('לא נמצאו תכונות להוספה');
      return;
    }

    await addNewClassListAttributesValuesOperations(
      buildingId,
      classId,
      selectedList.listId,
      values,
      setIsLoading,
      onHide,
      onSuccess,
    );
  };

  const renderAttributeInput = (attribute: any) => {
    const value = attributeValues[attribute.id];

    switch (attribute.attributeType) {
      case ClassAttributeType.Numeric:
        return (
          <InputNumber
            value={value?.numericValue || 0}
            onChange={(e) =>
              handleAttributeValueChange(attribute.id, e.value || 0)
            }
            className="w-full"
            placeholder="הכנס ערך מספרי"
          />
        );
      case ClassAttributeType.Bool:
        return (
          <InputSwitch
            checked={value?.boolValue || false}
            onChange={(e) => handleAttributeValueChange(attribute.id, e.value)}
          />
        );
      default:
        return <span>סוג תכונה לא נתמך: {attribute.attributeType}</span>;
    }
  };

  return (
    <Dialog
      header={`הוספת תכונות ל${className}`}
      visible={visible}
      onHide={onHide}
      style={{ width: '600px' }}
      footer={
        <div className="flex justify-content-end gap-2">
          <Button
            label="ביטול"
            icon="pi pi-times"
            onClick={onHide}
            severity="secondary"
            disabled={isLoading}
          />
          <Button
            label="אישור"
            icon="pi pi-check"
            onClick={handleSubmit}
            disabled={!selectedList || isLoading}
            loading={isLoading}
          />
        </div>
      }
    >
      <div className="flex flex-column gap-4">
        <div className="field">
          <label htmlFor="attributeList" className="block mb-2 font-bold">
            בחר רשימת תכונות:
          </label>
          <Dropdown
            id="attributeList"
            value={selectedList}
            options={classAttributeLists}
            onChange={(e) => setSelectedList(e.value)}
            optionLabel="listName"
            placeholder="בחר רשימת תכונות"
            className="w-full"
          />
        </div>

        {selectedList && (
          <Card
            title={`תכונות מרשימת "${selectedList.listName}"`}
            className="mt-3"
          >
            <div className="flex flex-column gap-3">
              {selectedList.attributes.map((attribute) => (
                <div key={attribute.id} className="field">
                  <div className="flex align-items-center justify-content-between mb-2">
                    <label className="font-medium">
                      {attribute.attributeName}
                    </label>
                    <span className="text-sm text-500">
                      (
                      {attribute.attributeType === ClassAttributeType.Numeric
                        ? 'מספר'
                        : 'בוליאני'}
                      )
                    </span>
                  </div>
                  {renderAttributeInput(attribute)}
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </Dialog>
  );
};
